// Shared display helpers for order payment/shipping info — used by
// OrderConfirmationPage, OrderStatusPage, and OrdersPage so all three show
// the exact same wording instead of each page guessing independently
// (which is how 'payu' previously fell through the cracks on the status
// page and showed no label at all).

export function getPaymentLabel(order) {
  const { payment, finalTotal = 0 } = order || {};
  const amount = `₹${finalTotal.toLocaleString('en-IN')}`;
  if (payment === 'cod') return `Cash on Delivery · ${amount}`;
  if (payment === 'razorpay' || payment === 'payu') return `Paid online · ${amount}`;
  return amount;
}

// Friendly name for the raw `payment` field (used where the page wants just
// the method name on its own, e.g. an account order's summary row).
export function getPaymentMethodName(order) {
  switch (order?.payment) {
    case 'cod':      return 'Cash on Delivery';
    case 'razorpay': return 'Razorpay (UPI, Cards, Net Banking, Wallets)';
    case 'payu':     return 'PayU (UPI, Cards, Net Banking, Wallets)';
    default:         return order?.payment || '—';
  }
}

// Gateway payment reference — whichever one of these the order actually has.
export function getPaymentReferenceId(order) {
  return order?.razorpayPaymentId || order?.payuPaymentId || null;
}

// Shipping method chosen at checkout (Standard / Express / Free, etc.) —
// falls back to 'Standard Shipping' for any older order created before
// shippingLabel was stored.
export function getShippingMethodLabel(order) {
  return order?.shippingLabel || 'Standard Shipping';
}

// Full shipping description: the method name plus its estimated delivery
// window, e.g. "Standard Shipping (3–5 business days)" or "Express
// Shipping (1 business day)" if min/max collapse to the same value.
// Falls back to just the label if no day estimate was stored on the order
// (e.g. older orders placed before this was tracked, or no delivery
// estimate rule matched at checkout).
export function getShippingMethodDescription(order) {
  const label = getShippingMethodLabel(order);
  const { shippingMinDays, shippingMaxDays } = order || {};

  if (shippingMinDays == null && shippingMaxDays == null) return label;

  const min = shippingMinDays ?? shippingMaxDays;
  const max = shippingMaxDays ?? shippingMinDays;
  const days = min === max
    ? `${min} business day${min === 1 ? '' : 's'}`
    : `${min}–${max} business days`;

  return `${label} (${days})`;
}